import { GoalFilterDocumentKeys, GoalRoadmapSortingKeys } from '@griply/types';
import { buildGoalOverviewReorderUpdate } from '@griply/utils';
import { useLocalizedLifeAreas, useUpdateLifeArea } from 'features/life-areas';
import { useUpdateUserSettings } from 'features/user';
import { useCallback } from 'react';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useUser } from 'shared/hooks/use-user';
import {
  GoalLifeAreaLinkChange,
  GoalParentLinkChange,
  GoalReorderSectionUpdate,
  UpdateGoal,
} from 'shared/types/goal';
import { GoalGroupBy } from 'shared/types/sorting-mode';

import { useCreateUnlimitedGoalsAllowed } from './use-create-unlimited-goals-allowed';
import { useMergeGoalLinkChanges } from './use-merge-goal-link-changes';
import { useSortedGoals } from './use-sorted-goals';
import { useUpdateGoal } from './use-update-goal';

// Parallel to useLifeAreaGoalReorder / useSubGoalReorder: persists to the user's roadmap-order setting.
export const useGoalOverviewReorder = (
  groupBy: GoalGroupBy,
  filterDocumentKey = GoalFilterDocumentKeys.GoalsOverview,
) => {
  const { submit: updateGoal } = useUpdateGoal();
  const mergeGoalLinkChanges = useMergeGoalLinkChanges();
  const { submit: updateLifeArea } = useUpdateLifeArea();
  const updateSettings = useUpdateUserSettings();
  const sortedGoals = useSortedGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const links = useEntityLinks();
  const user = useUser();

  // needed for de-archiving logic
  const { allowed } = useCreateUnlimitedGoalsAllowed();

  return useCallback(
    (
      updatedSections: GoalReorderSectionUpdate[],
      updatedGoals: UpdateGoal[],
      goalLinkChanges?: GoalLifeAreaLinkChange[],
      goalParentChanges?: GoalParentLinkChange[],
    ) => {
      const { goalUpdates, lifeAreaUpdates, settingsUpdate } =
        buildGoalOverviewReorderUpdate({
          updatedSections,
          updatedGoals,
          goals: sortedGoals,
          lifeAreas,
          links,
          groupBy,
          allowed,
          currentGoalGroupSorting:
            user?.settings?.goalGroupSorting?.lifeArea?.sorting,
          currentRoadmapSorting:
            user?.settings?.goalRoadmapSorting?.[
              GoalRoadmapSortingKeys.InProgress
            ],
          filterDocumentKey,
        });

      updateSettings(settingsUpdate);
      lifeAreaUpdates.forEach((lifeArea) => updateLifeArea(lifeArea));
      mergeGoalLinkChanges(goalUpdates, {
        goalParentChanges,
        goalLifeAreaChanges: goalLinkChanges,
      }).forEach((goal) => updateGoal(goal));
    },
    [
      mergeGoalLinkChanges,
      groupBy,
      filterDocumentKey,
      sortedGoals,
      lifeAreas,
      links,
      user?.settings?.goalGroupSorting?.lifeArea?.sorting,
      user?.settings?.goalRoadmapSorting,
      allowed,
      updateSettings,
      updateLifeArea,
      updateGoal,
    ],
  );
};
